import React, { useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import Toast from '../Toast';

const FeedbackForm = ({ orderId, order, onClose, onSubmitted }) => {
    const [formData, setFormData] = useState({
        foodRating: null,
        staffRating: null,
        restaurantRating: null,
        comment: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleRatingChange = (type, rating) => {
        setFormData(prev => ({ ...prev, [type]: rating }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Prevent double submission
        if (submitting) {
            return;
        }
        
        // All ratings are optional, so we can submit even if none are provided
        try {
            setSubmitting(true);
            const response = await axios.post(API_ENDPOINTS.FEEDBACK_CREATE, {
                orderId,
                foodRating: formData.foodRating,
                staffRating: formData.staffRating,
                restaurantRating: formData.restaurantRating,
                comment: formData.comment,
                customerName: order?.customerName || '',
                customerPhone: order?.mobileNumber || ''
            });
            
            showToast('Thank you for your feedback!', 'success');
            
            // Call onSubmitted callback immediately to update parent state
            if (onSubmitted) {
                onSubmitted();
            }
            
            // Close the form after a short delay
            setTimeout(() => {
                if (onClose) onClose();
            }, 1000);
        } catch (error) {
            // Check if it's a duplicate feedback error
            if (error.response?.status === 400 && 
                error.response?.data?.message?.includes('already submitted')) {
                showToast('Feedback has already been submitted for this order', 'error');
                // Still call onSubmitted to update the UI state
                if (onSubmitted) {
                    onSubmitted();
                }
                setTimeout(() => {
                    if (onClose) onClose();
                }, 1500);
            } else {
                showToast(error.response?.data?.message || 'Failed to submit feedback', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const renderStarRating = (type, label) => {
        const currentRating = formData[type];
        return (
            <div className="mb-4">
                <label className="block text-sm font-medium mb-2">{label} (Optional)</label>
                <div className="flex items-center space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            type="button"
                            onClick={() => handleRatingChange(type, star)}
                            className={`text-3xl transition-transform hover:scale-110 ${
                                currentRating >= star ? 'text-yellow-400' : 'text-gray-300'
                            }`}
                        >
                            ★
                        </button>
                    ))}
                    {currentRating && (
                        <span className="ml-2 text-sm text-gray-600">({currentRating}/5)</span>
                    )}
                    {currentRating && (
                        <button
                            type="button"
                            onClick={() => handleRatingChange(type, null)}
                            className="ml-2 text-sm text-red-600 hover:text-red-800"
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 p-4 flex items-center justify-center"
            style={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflowY: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh'
            }}
        >
            <div 
                className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                style={{ 
                    margin: 'auto',
                    position: 'relative'
                }}
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold">Share Your Feedback</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-2xl"
                    >
                        ×
                    </button>
                </div>

                <p className="text-gray-600 mb-6">
                    We'd love to hear about your experience! Your feedback helps us improve.
                </p>

                <form onSubmit={handleSubmit}>
                    {renderStarRating('foodRating', 'Food Rating')}
                    {renderStarRating('staffRating', 'Staff/Service Rating')}
                    {renderStarRating('restaurantRating', 'Restaurant Rating')}

                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Comments (Optional)</label>
                        <textarea
                            value={formData.comment}
                            onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                            rows="4"
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Tell us more about your experience..."
                        />
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                            disabled={submitting}
                        >
                            Skip
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            disabled={submitting}
                        >
                            {submitting ? 'Submitting...' : 'Submit Feedback'}
                        </button>
                    </div>
                </form>

                {toast.show && <Toast message={toast.message} type={toast.type} />}
            </div>
        </div>
    );
};

export default FeedbackForm;

